import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { config } from '../config';
import './OrderSummary.css';

interface AvailableTicket {
  ticketType: string;
  eventType: string;
  pricePerTicket: number;
  totalAvailable: number;
  eventDate: string | null;
  description?: string | null;
  venue?: string | null;
}

interface CartItem extends AvailableTicket {
  quantity: number;
}

type PaymentMethod = 'razorpay' | 'payglocal' | 'paytmpg';

// Declare Razorpay and Paytm on window for TypeScript
declare global {
  interface Window {
    Razorpay: any;
    Paytm: any;
  }
}

// Dynamically load the Razorpay checkout script
function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// Dynamically load the Paytm checkout script and wait for the full SDK to be ready
function loadPaytmScript(mid: string, env: string): Promise<boolean> {
  return new Promise((resolve) => {
    const scriptId = 'paytm-checkout-js';

    // Step 1: Inject the wrapper script tag if not already present
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      const host = env === 'prod' ? 'secure.paytmpayments.com' : 'securestage.paytmpayments.com';
      script.src = `https://${host}/merchantpgpui/checkoutjs/merchants/${mid}.js`;
      script.crossOrigin = 'anonymous';
      script.onerror = () => {
        console.error('[ERROR] Paytm wrapper script failed to load');
        resolve(false);
      };
      document.body.appendChild(script);
      console.log('[DEBUG] Paytm wrapper script tag injected');
    } else {
      console.log('[DEBUG] Paytm wrapper script tag already exists');
    }

    // Step 2: Poll until the REAL init function is available
    // The wrapper script loads, then it internally fetches the actual CheckoutJS via XHR.
    // We must wait for that secondary load to complete.
    const TIMEOUT_MS = 15000;
    const POLL_MS = 100;
    const start = Date.now();

    const poll = () => {
      if (
        window.Paytm &&
        window.Paytm.CheckoutJS &&
        typeof window.Paytm.CheckoutJS.init === 'function'
      ) {
        console.log(`[DEBUG] Paytm CheckoutJS fully ready in ${Date.now() - start}ms`);
        resolve(true);
        return;
      }
      if (Date.now() - start > TIMEOUT_MS) {
        console.error(`[ERROR] Paytm CheckoutJS not ready after ${TIMEOUT_MS}ms timeout`);
        resolve(false);
        return;
      }
      setTimeout(poll, POLL_MS);
    };
    poll();
  });
}

export function OrderSummary() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { cartItems?: CartItem[]; grandTotal?: number } | null;

  const [cartItems, setCartItems] = useState<CartItem[]>(state?.cartItems || []);
  const [grandTotal, setGrandTotal] = useState<number>(state?.grandTotal || 0);
  const [loading, setLoading] = useState(!state?.cartItems);
  const [paying, setPaying] = useState(false);

  // Buyer info
  const [buyerName, setBuyerName] = useState(() => sessionStorage.getItem('luminar_buyer_name') || '');
  const [buyerEmail, setBuyerEmail] = useState(() => sessionStorage.getItem('luminar_buyer_email') || '');
  const [buyerPhone, setBuyerPhone] = useState(() => sessionStorage.getItem('luminar_buyer_phone') || '');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('payglocal');

  // Persist contact details in session
  useEffect(() => {
    sessionStorage.setItem('luminar_buyer_name', buyerName);
  }, [buyerName]);

  useEffect(() => {
    sessionStorage.setItem('luminar_buyer_email', buyerEmail);
  }, [buyerEmail]);

  useEffect(() => {
    sessionStorage.setItem('luminar_buyer_phone', buyerPhone);
  }, [buyerPhone]);

  // Preload Paytm Checkout JS SDK on mount to eliminate invocation delay
  useEffect(() => {
    const mid = 'xAOrCH67914450897373';
    const env = 'staging';
    loadPaytmScript(mid, env)
      .then((success) => {
        if (success) {
          console.log('[DEBUG] Paytm SDK preloaded successfully on mount');
        } else {
          console.warn('[WARNING] Paytm SDK failed to preload on mount');
        }
      });
  }, []);

  const cartKey = (t: AvailableTicket) => `${t.ticketType}__${t.eventType}__${t.pricePerTicket}__${t.eventDate || ''}__${t.venue || ''}`;

  // Reconstruct cart if page reloaded
  const reconstructCart = useCallback(async () => {
    if (state?.cartItems) return;
    setLoading(true);
    try {
      const savedCartRaw = sessionStorage.getItem('luminar_cart');
      if (!savedCartRaw) {
        setLoading(false);
        return;
      }
      const savedCart: Record<string, number> = JSON.parse(savedCartRaw);

      const res = await fetch(`${config.API_BASE_URL}/tickets/available`);
      if (!res.ok) throw new Error('Failed to load tickets');
      const tickets: AvailableTicket[] = await res.json();

      const items: CartItem[] = tickets
        .map((t) => ({ ...t, quantity: savedCart[cartKey(t)] || 0 }))
        .filter((t) => t.quantity > 0);

      const total = items.reduce((sum, t) => sum + t.pricePerTicket * t.quantity, 0);

      setCartItems(items);
      setGrandTotal(total);
    } catch (err) {
      console.error('Error reconstructing checkout cart:', err);
    } finally {
      setLoading(false);
    }
  }, [state]);

  useEffect(() => {
    reconstructCart();
  }, [reconstructCart]);

  // ─── Razorpay payment handler ────────────────────────────────────────────────
  const handleRazorpayPayment = async () => {
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      throw new Error('Could not load Razorpay. Please check your connection.');
    }

    const allTicketIds: string[] = [];

    for (const item of cartItems) {
      // 1. Create Razorpay order on backend
      const res = await fetch(`${config.API_BASE_URL}/tickets/initiate-razorpay-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketType: item.ticketType,
          eventType: item.eventType,
          quantity: item.quantity,
          pricePerTicket: item.pricePerTicket,
          buyerName,
          buyerEmail,
          buyerPhone,
        }),
      });

      if (!res.ok) throw new Error('Failed to create Razorpay order');
      const data = await res.json();

      // Mock mode fallback
      if (data.mockMode) {
        const mockRes = await fetch(`${config.API_BASE_URL}/tickets/confirm-mock-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticketType: item.ticketType,
            eventType: item.eventType,
            quantity: item.quantity,
            pricePerTicket: item.pricePerTicket,
            buyerName,
            buyerEmail,
            buyerPhone,
          }),
        });
        if (!mockRes.ok) throw new Error('Ticket creation failed');
        const created: { id: string }[] = await mockRes.json();
        allTicketIds.push(...created.map((t) => t.id));
        continue;
      }

      // 2. Open Razorpay checkout popup
      await new Promise<void>((resolve, reject) => {
        let handlerCalled = false;

        const options = {
          key: data.razorpayKeyId,
          amount: Math.round(item.pricePerTicket * item.quantity * 100), // in paise
          currency: 'INR',
          name: 'Luminar Entertainment',
          description: `${item.ticketType.toUpperCase()} - ${item.eventType.toUpperCase()} Event`,
          order_id: data.razorpayOrderId,
          prefill: {
            name: buyerName,
            email: buyerEmail,
            contact: buyerPhone,
          },
          theme: {
            color: '#e10a1e',
          },
          modal: {
            ondismiss: () => {
              if (!handlerCalled) {
                reject(new Error('Payment cancelled by user'));
              }
            },
          },
          handler: async (response: {
            razorpay_order_id: string;
            razorpay_payment_id: string;
            razorpay_signature: string;
          }) => {
            handlerCalled = true;
            try {
              // 3. Verify & create tickets on backend
              const verifyRes = await fetch(
                `${config.API_BASE_URL}/tickets/verify-razorpay-payment`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                    ticketType: item.ticketType,
                    eventType: item.eventType,
                    quantity: item.quantity,
                    pricePerTicket: item.pricePerTicket,
                    buyerName,
                    buyerEmail,
                    buyerPhone,
                  }),
                },
              );

              if (!verifyRes.ok) {
                const err = await verifyRes.json();
                reject(new Error(err.message || 'Verification failed'));
                return;
              }

              const result = await verifyRes.json();
              result.tickets.forEach((t: { id: string }) => allTicketIds.push(t.id));
              resolve();
            } catch (err: any) {
              reject(err);
            }
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (response: any) => {
          reject(
            new Error(
              response?.error?.description ||
              response?.error?.reason ||
              'Payment failed',
            ),
          );
        });
        rzp.open();
      });
    }

    // Navigate to success page
    navigate('/payment-success', {
      state: {
        ticketIds: allTicketIds,
        buyerName,
        buyerEmail,
        buyerPhone,
        totalAmount: grandTotal,
      },
    });
  };

  // ─── PayGlocal payment handler ────────────────────────────────────────────────
  const handlePayglocalPayment = async () => {
    const firstItem = cartItems[0];

    const res = await fetch(`${config.API_BASE_URL}/tickets/initiate-payglocal-payment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ticketType: firstItem.ticketType,
        eventType: firstItem.eventType,
        quantity: firstItem.quantity,
        pricePerTicket: firstItem.pricePerTicket,
        buyerName,
        buyerEmail,
        buyerPhone,
      }),
    });

    if (!res.ok) throw new Error('PayGlocal payment initiation failed');
    const data = await res.json();

    // Mock mode fallback
    if (data.mockMode) {
      const allTicketIds: string[] = [];
      for (const item of cartItems) {
        const mockRes = await fetch(`${config.API_BASE_URL}/tickets/confirm-mock-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticketType: item.ticketType,
            eventType: item.eventType,
            quantity: item.quantity,
            pricePerTicket: item.pricePerTicket,
            buyerName,
            buyerEmail,
            buyerPhone,
          }),
        });
        if (!mockRes.ok) throw new Error('Ticket creation failed');
        const created: { id: string }[] = await mockRes.json();
        allTicketIds.push(...created.map((t) => t.id));
      }
      navigate('/payment-success', {
        state: {
          ticketIds: allTicketIds,
          buyerName,
          buyerEmail,
          buyerPhone,
          totalAmount: grandTotal,
        },
      });
      return;
    }

    if (data.payglobalRedirectUrl) {
      window.location.href = data.payglobalRedirectUrl;
    } else {
      throw new Error('No redirect URL received from PayGlocal');
    }
  };

  // ─── Paytm payment handler ──────────────────────────────────────────────────
  const handlePaytmPayment = async () => {
    const firstItem = cartItems[0];
    const mid = 'xAOrCH67914450897373';
    const env = 'staging';

    // Run backend API call and SDK loading IN PARALLEL to save time
    const [apiResult, sdkReady] = await Promise.all([
      fetch(`${config.API_BASE_URL}/tickets/initiate-paytm-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketType: firstItem.ticketType,
          eventType: firstItem.eventType,
          quantity: firstItem.quantity,
          pricePerTicket: firstItem.pricePerTicket,
          buyerName,
          buyerEmail,
          buyerPhone,
        }),
      }),
      loadPaytmScript(mid, env),
    ]);

    if (!apiResult.ok) throw new Error('Paytm payment initiation failed');
    const data = await apiResult.json();

    // Mock mode fallback
    if (data.mockMode) {
      const allTicketIds: string[] = [];
      for (const item of cartItems) {
        const mockRes = await fetch(`${config.API_BASE_URL}/tickets/confirm-mock-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticketType: item.ticketType,
            eventType: item.eventType,
            quantity: item.quantity,
            pricePerTicket: item.pricePerTicket,
            buyerName,
            buyerEmail,
            buyerPhone,
          }),
        });
        if (!mockRes.ok) throw new Error('Ticket creation failed');
        const created: { id: string }[] = await mockRes.json();
        allTicketIds.push(...created.map((t) => t.id));
      }
      navigate('/payment-success', {
        state: {
          ticketIds: allTicketIds,
          buyerName,
          buyerEmail,
          buyerPhone,
          totalAmount: grandTotal,
        },
      });
      return;
    }

    if (!data.paytmTxnToken || !data.paytmMid) {
      throw new Error('Invalid response received from Paytm server');
    }

    if (!sdkReady) {
      throw new Error('Failed to load Paytm checkout SDK. Please try again.');
    }

    // SDK is guaranteed ready by loadPaytmScript — call init() directly
    const paytmConfig = {
      root: '',
      flow: 'DEFAULT',
      data: {
        orderId: data.paytmOrderId,
        token: data.paytmTxnToken,
        tokenType: 'TXN_TOKEN',
        amount: data.amount,
      },
      merchant: {
        mid: data.paytmMid,
        redirect: true,
      },
      handler: {
        notifyMerchant: function (eventName: string, eventData: any) {
          console.log('Paytm Event:', eventName, eventData);
        },
      },
    };

    console.log('[DEBUG] Calling Paytm CheckoutJS.init() directly');
    await window.Paytm.CheckoutJS.init(paytmConfig);
    console.log('[DEBUG] Paytm CheckoutJS.init() succeeded, invoking gateway');
    window.Paytm.CheckoutJS.invoke();
  };

  // ─── Pay submit handler ──────────────────────────────────────────────────────
  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cartItems.length === 0) return;

    setPaying(true);
    try {
      if (paymentMethod === 'razorpay') {
        await handleRazorpayPayment();
      } else if (paymentMethod === 'payglocal') {
        await handlePayglocalPayment();
      } else {
        await handlePaytmPayment();
      }
    } catch (err: any) {
      const reason = encodeURIComponent(
        err?.message || 'Payment could not be completed. Please try again.',
      );
      navigate(`/payment-failed?reason=${reason}`);
    } finally {
      setPaying(false);
    }
  };

  const hasItems = cartItems.length > 0;

  if (loading) {
    return (
      <div className="os-page-loading">
        <div className="os-spinner" />
        <span>Loading order details…</span>
      </div>
    );
  }

  return (
    <div className="os-page">
      {/* Ambient background */}
      <div className="os-bg">
        <div className="os-bg__orb os-bg__orb--1" />
        <div className="os-bg__orb os-bg__orb--2" />
        <div className="os-bg__grid" />
      </div>

      <div className="os-container">
        {/* Header */}
        <header className="os-header">
          <Link to="/book-tickets" className="os-back">← Back to Ticket Selection</Link>
          <div className="os-header__text">
            <span className="os-eyebrow">Checkout</span>
            <h1 className="os-title">Order <span className="os-title__accent">Summary</span></h1>
          </div>
        </header>

        {!hasItems ? (
          <div className="os-empty">
            <span className="os-empty__icon">🎟️</span>
            <h3>No tickets selected</h3>
            <p>Go back to the booking page to select your tickets.</p>
            <Link to="/book-tickets" className="os-empty__btn">Select Tickets</Link>
          </div>
        ) : (
          <form onSubmit={handlePay} className="os-checkout-card">

            {/* Left Side - Booking Summary */}
            <div className="os-checkout-summary">
              <h2 className="os-checkout-title">Booking Summary</h2>

              <ul className="os-items-list">
                {cartItems.map((item) => (
                  <li key={cartKey(item)} className="os-item">
                    <div className="os-item__left">
                      <span className="os-item__type">{item.ticketType.toUpperCase()}</span>
                      <span className="os-item__event">{item.eventType} Event</span>
                      <span className="os-item__qty">Qty: {item.quantity} × ₹{item.pricePerTicket.toLocaleString('en-IN')}</span>
                    </div>
                    <span className="os-item__price">
                      ₹{(item.pricePerTicket * item.quantity).toLocaleString('en-IN')}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="os-summary-divider" />

              <div className="os-summary-total">
                <span>Total Amount</span>
                <span className="os-summary-total-val">₹{grandTotal.toLocaleString('en-IN')}</span>
              </div>
            </div>

            {/* Right Side - Form Details & Payment Selector */}
            <div className="os-checkout-details">

              {/* Contact Information */}
              <div className="os-section">
                <h3 className="os-section-title">
                  <span className="os-section-num">1</span>
                  Share your Contact Details
                </h3>

                <div className="os-form-grid">
                  <div className="os-field">
                    <label htmlFor="buyer-name">Full Name *</label>
                    <input
                      id="buyer-name"
                      type="text"
                      placeholder="John Doe"
                      value={buyerName}
                      onChange={(e) => setBuyerName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="os-form-row">
                    <div className="os-field">
                      <label htmlFor="buyer-email">Email Address *</label>
                      <input
                        id="buyer-email"
                        type="email"
                        placeholder="john@example.com"
                        value={buyerEmail}
                        onChange={(e) => setBuyerEmail(e.target.value)}
                        required
                      />
                    </div>

                    <div className="os-field">
                      <label htmlFor="buyer-phone">Phone Number *</label>
                      <input
                        id="buyer-phone"
                        type="tel"
                        placeholder="+91 98765 43210"
                        value={buyerPhone}
                        onChange={(e) => setBuyerPhone(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Selector */}
              <div className="os-section">
                <h3 className="os-section-title">
                  <span className="os-section-num">2</span>
                  Select Payment Method
                </h3>

                <div className="os-payment-options">
                  <button
                    type="button"
                    className={`os-pm-btn ${paymentMethod === 'payglocal' ? 'os-pm-btn--active' : ''}`}
                    onClick={() => setPaymentMethod('payglocal')}
                  >
                    <span className="os-pm-btn__logo">
                      <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="28">
                        <rect width="48" height="48" rx="8" fill="#1A6FE8" />
                        <text x="5" y="30" fontFamily="Arial" fontWeight="bold" fontSize="11" fill="white">Pay</text>
                        <text x="5" y="42" fontFamily="Arial" fontWeight="bold" fontSize="9" fill="#93C5FD">Glocal</text>
                      </svg>
                    </span>
                    <div className="os-pm-btn__info">
                      <span className="os-pm-btn__name">PayGlocal</span>
                      <span className="os-pm-btn__desc">International Cards &amp; Apple Pay</span>
                    </div>
                    {paymentMethod === 'payglocal' && <span className="os-pm-btn__check">✓</span>}
                  </button>

                  <button
                    type="button"
                    className={`os-pm-btn ${paymentMethod === 'paytmpg' ? 'os-pm-btn--active' : ''}`}
                    onClick={() => setPaymentMethod('paytmpg')}
                  >
                    <span className="os-pm-btn__logo">
                      <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="28">
                        <rect width="48" height="48" rx="8" fill="#002970" />
                        <text x="5" y="30" fontFamily="Arial" fontWeight="bold" fontSize="11" fill="white">Pay</text>
                        <text x="5" y="42" fontFamily="Arial" fontWeight="bold" fontSize="11" fill="#00BAF2">tm</text>
                      </svg>
                    </span>
                    <div className="os-pm-btn__info">
                      <span className="os-pm-btn__name">Paytm</span>
                      <span className="os-pm-btn__desc">UPI, Cards, and Netbanking</span>
                    </div>
                    {paymentMethod === 'paytmpg' && <span className="os-pm-btn__check">✓</span>}
                  </button>
                </div>
              </div>

              {/* Pay Button Area */}
              <div className="os-action-area">
                <button
                  type="submit"
                  className="os-pay-btn"
                  disabled={paying}
                >
                  {paying ? (
                    <><div className="os-spinner os-spinner--sm" /> Processing…</>
                  ) : (
                    <>
                      <span>🔒 Pay ₹{grandTotal.toLocaleString('en-IN')}</span>
                      <span className="os-pay-btn__arrow">→</span>
                    </>
                  )}
                </button>

                <p className="os-secure-note">
                  By clicking pay, you agree to our Terms &amp; Conditions. All transactions are fully encrypted and secure.
                </p>
              </div>

            </div>

          </form>
        )}
      </div>
    </div>
  );
}
